import { useLocation } from "@reach/router";
import {
	useStoryblokState,
	type ISbStoryData,
	type ISbStoryParams,
	type SbGatsbyStory,
} from "gatsby-source-storyblok";
import { useCallback, useEffect, useState } from "react";
import StoryblokClient from "storyblok-js-client";
import { getStoryblokStoryConfig } from "../../config/storyblok-config";
import { isStoryblokPreview } from "../../environment.config.mjs";

const getStoryUidFromStoryblokParams = (search: string) => {
	const urlParams = new URLSearchParams(search);
	return Number.parseInt(urlParams.get("_storyblok") ?? "0");
};

const getLangFromStoryblokParams = (search: string) => {
	const urlParams = new URLSearchParams(search);
	return urlParams.get("_storyblok_lang") ?? "";
};

export const useStoryblokPreviewData = <T extends ISbStoryData["content"]>(
	initialStory?: T,
): T | undefined => {
	const [content, setContent] = useState<T | undefined>(initialStory);
	const { search } = useLocation();

	const uid = getStoryUidFromStoryblokParams(search);
	const lang = getLangFromStoryblokParams(search);

	const { content: stateContent } = useStoryblokState(
		{
			content: undefined,
			internalId: getStoryUidFromStoryblokParams(search),
		} as unknown as SbGatsbyStory,
		{
			language: getLangFromStoryblokParams(search),
		},
	) as unknown as { content: typeof initialStory };

	const getInitialStory = useCallback(async () => {
		console.log("Fetching initial story");

		if (!uid || !lang) return;

		const Storyblok = new StoryblokClient({
			accessToken: process.env.GATSBY_STORYBLOK_TOKEN,
		});

		const params: ISbStoryParams = {
			...getStoryblokStoryConfig(),
			language: lang,
			version: "draft",
			cv: Storyblok.cacheVersion(),
		};

		const {
			data: { story },
		} = await Storyblok.getStory(uid.toString(), params);

		return story as
			| (ISbStoryData & {
					[index: string]: unknown;
			  })
			| undefined;
	}, [uid, lang]);

	// This will make it so that when developing and the draft data changes in storyblok you wont need to rebuild.
	// This might cause some flickering of content but that is a development only issue and goes away if you rebuild the site as well.
	useEffect(() => {
		if (!isStoryblokPreview()) return;

		getInitialStory()
			.then((story) => {
				if (story?.content) {
					story.content.name = story.name;
					setContent(story.content as T);
				}
			})
			.catch((error) => {
				console.error("Error while fetching initial story", error);
			});
	}, [getInitialStory]);

	return stateContent ? { ...initialStory, ...stateContent } : content;
};
